// Ported from deexoptions-mobile-app/utils/activity/normalizeTransaction.ts.
// Spec: deexoptions-mobile-app/docs/activity-system.md
//
// Three deliberate divergences from mobile, each marked "PWA:" below:
//   1. formatAmount replaces mobile's formatPrice/formatCryptoPrice.
//   2. Gift cards ARE stamped with a transactionType (mobile leaves them bare).
//   3. Bill payments read extra electricity field paths the PWA's payloads use.

import { formatAmount } from "../formatTime";
import type { ActivityFilter, ActivityType } from "./filters";

export type ActivityItemStatus =
  | "success"
  | "pending"
  | "in progress"
  | "failed";

export interface ActivityIconMeta {
  kind: "coin" | "utility" | "giftcard" | "bank";
  value: string;
  isElectricity?: boolean;
}

export interface ActivityItem {
  id: string;
  type: ActivityType;
  subtype?: string;
  title: string;
  amountPrimary: string;
  amountSecondary?: string;
  status: ActivityItemStatus;
  createdAt: string;
  iconMeta: ActivityIconMeta;
  raw: any;
}

// PENDING also covers backend PROCESSING; records with no status field at all
// (crypto deposits, some legacy betting rows) are settled transactions.
//
// This keeps the *raw* state — including "failed" — because routing and icons
// branch on it. Masking failed → pending is a display concern handled by
// toDisplayStatus at render time; don't mask here.
export const normalizeStatus = (raw?: string): ActivityItemStatus => {
  const status = raw?.toUpperCase();
  if (!status) return "success";
  if (status === "COMPLETED" || status === "SUCCESS") return "success";
  if (status === "FAILED") return "failed";
  if (status === "PROCESSING" || status === "IN PROGRESS") return "in progress";
  if (status === "PENDING") return "pending";
  return "pending";
};

const upper = (v?: string) => v?.toUpperCase() ?? "";

// The ActivityDetailsModal receipt branches on the legacy feed's
// transactionType field, which /api/transactions/me items don't carry —
// stamp it onto the raw object so the existing receipt renders unchanged.
const withTransactionType = (tx: any, transactionType: string) => ({
  ...tx,
  transactionType,
});

// PWA: mobile uses formatCryptoPrice with a per-coin decimals table. The PWA has
// no such table; formatAmount already scales precision for sub-cent values. The
// `coin` param is kept so a decimals table can drop in later without touching
// callers.
const formatCoinAmount = (amount: number, _coin?: string) =>
  formatAmount(amount);

const normalizeCryptoSell = (tx: any): ActivityItem => ({
  id: tx._id,
  type: "crypto",
  subtype: "sell",
  title: `${upper(tx.asset)} — Sell`,
  amountPrimary: `${formatCoinAmount(tx.amount, tx.asset)} ${upper(tx.asset)}`,
  amountSecondary: tx.amountNGN
    ? `₦${formatAmount(tx.amountNGN)}`
    : tx.amountUSDT
    ? `$${formatAmount(tx.amountUSDT)}`
    : undefined,
  status: normalizeStatus(tx.status),
  createdAt: tx.createdAt,
  iconMeta: { kind: "coin", value: upper(tx.asset) },
  // PWA: `sellAsset` mirrors the payload's own string `asset`. The receipt
  // modal's transaction type already declares `asset` as the payout object
  // shape ({ amountInUSD, name, amount }); aliasing under a distinct name keeps
  // the sell branch type-safe without forcing a union narrow at every payout
  // callsite.
  raw: { ...withTransactionType(tx, "deex-crypto-sells"), sellAsset: tx.asset },
});

const normalizeCryptoDeposit = (tx: any): ActivityItem => ({
  id: tx._id,
  type: "crypto",
  subtype: "deposit",
  title: `${upper(tx.currency)} — Deposit`,
  amountPrimary: `${formatCoinAmount(tx.amount, tx.currency)} ${upper(
    tx.currency
  )}`,
  status: normalizeStatus(tx.status),
  createdAt: tx.createdAt,
  iconMeta: { kind: "coin", value: upper(tx.currency) },
  raw: withTransactionType(tx, "deex-deposits"),
});

const normalizeCryptoSwap = (tx: any): ActivityItem => ({
  id: tx._id,
  type: "crypto",
  subtype: "swap",
  title: `${upper(tx.from?.asset)} to ${upper(tx.to?.asset)}`,
  amountPrimary: `${formatCoinAmount(tx.from?.amount, tx.from?.asset)} ${upper(
    tx.from?.asset
  )}`,
  amountSecondary: `${formatCoinAmount(tx.to?.amount, tx.to?.asset)} ${upper(
    tx.to?.asset
  )}`,
  status: normalizeStatus(tx.status),
  createdAt: tx.createdAt,
  iconMeta: { kind: "coin", value: upper(tx.from?.asset) },
  raw: withTransactionType(tx, "deex-asset-swaps"),
});

const normalizeCryptoWithdraw = (tx: any): ActivityItem => ({
  id: tx._id,
  type: "crypto",
  subtype: "withdraw",
  title: `${upper(tx.currency)} — ${tx.toDeexUser ? "Transfer" : "Withdrawal"}`,
  amountPrimary: `${formatCoinAmount(tx.amount, tx.currency)} ${upper(
    tx.currency
  )}`,
  amountSecondary:
    tx.usdtAmount != null ? `$${formatAmount(tx.usdtAmount)}` : undefined,
  status: normalizeStatus(tx.status),
  createdAt: tx.createdAt,
  iconMeta: { kind: "coin", value: upper(tx.currency) },
  raw: withTransactionType(tx, "deex-crypto-withdrawals"),
});

const BILL_TITLES: Record<string, string> = {
  airtime: "Airtime",
  data: "Data",
  electricity: "Electricity",
  betting: "Betting",
};

const normalizeBillPayment = (tx: any, subtype: string): ActivityItem => {
  const data = tx.utility?.data;
  const isElectricity = subtype === "electricity";
  // PWA: electricity payloads here carry the disco under metaData.disco /
  // receiver.distribution / electricity, none of which mobile's path list
  // covers — without these every electricity row falls back to the bolt icon.
  const network = isElectricity
    ? data?.metaData?.disco ??
      data?.receiver?.distribution ??
      data?.electricity ??
      data?.network ??
      data?.service ??
      data?.provider
    : data?.network ?? data?.service ?? data?.provider;
  const target =
    data?.phone ?? data?.receiver?.number ?? data?.meter_number ?? "";
  const title = network
    ? `${upper(network)} ${BILL_TITLES[subtype] ?? ""}${
        target ? ` · ${target}` : ""
      }`
    : BILL_TITLES[subtype] ?? "Bill payment";
  const ngn = tx.amountNGN || data?.amount;
  return {
    id: tx._id,
    type: "billpayment",
    subtype,
    title,
    amountPrimary: ngn
      ? `₦${formatAmount(ngn)}`
      : `${tx.amount ?? 0} ${upper(tx.currency)}`,
    amountSecondary:
      tx.usdtAmount != null ? `$${formatAmount(tx.usdtAmount)}` : undefined,
    status: normalizeStatus(tx.status),
    createdAt: tx.createdAt,
    iconMeta: {
      kind: "utility",
      value: (network ?? "").toLowerCase(),
      isElectricity,
    },
    raw: withTransactionType(tx, "deex-crypto-withdrawals"),
  };
};

const sumTradeValue = (tradeValue?: { amount: number }[]) =>
  tradeValue?.reduce((total, item) => total + (item.amount ?? 0), 0) ?? 0;

const normalizeGiftCard = (tx: any): ActivityItem => ({
  id: tx._id,
  type: "giftcard",
  title: tx.card?.name ?? "Gift card",
  amountPrimary: `$${formatAmount(sumTradeValue(tx.tradeValue))}`,
  amountSecondary: tx.country,
  status: normalizeStatus(tx.status),
  createdAt: tx.createdAt,
  iconMeta: { kind: "giftcard", value: tx.card?.id ?? "" },
  // PWA: mobile leaves gift cards unstamped because it routes them to a
  // separate screen (/gift-card-feedback). The PWA renders them in the same
  // ActivityDetailsModal, which keys off transactionType — without this stamp
  // every gift card opens a blank receipt.
  raw: withTransactionType(tx, "gift-card-transactions"),
});

// Bank names can be verbose ("OPay Digital Services Limited (OPay)") —
// prefer the short parenthesised name when present.
const shortBankName = (name?: string) => {
  if (!name) return undefined;
  const match = name.match(/\(([^)]+)\)\s*$/);
  return match ? match[1] : name;
};

const normalizePayout = (tx: any): ActivityItem => ({
  id: tx._id,
  type: "payout",
  title: `Payout — ${shortBankName(tx.recipient?.bankName) ?? tx.provider ?? ""}`,
  amountPrimary: `₦${formatAmount(tx.payoutAmount)}`,
  amountSecondary:
    tx.asset?.amountInUSD != null
      ? `$${formatAmount(tx.asset.amountInUSD)}`
      : undefined,
  status: normalizeStatus(tx.status),
  createdAt: tx.createdAt,
  iconMeta: { kind: "bank", value: upper(tx.asset?.name) },
  raw: withTransactionType(tx, "payouts"),
});

const KNOWN_TYPES: ActivityType[] = [
  "crypto",
  "giftcard",
  "billpayment",
  "payout",
  "card",
];

/**
 * Works out which normalizer a row from the mixed `/recent` feed needs.
 *
 * `/recent` stamps each row with `type` (+ `subtype` for crypto), so that is
 * trusted first. Two guards around it:
 *
 * - `utility` is checked *before* the stamp, because bill payments live in the
 *   crypto-withdrawals collection and carry `transactionType:
 *   "deex-crypto-withdrawals"` — trusting the stamp first would render an
 *   airtime purchase as a coin withdrawal.
 * - The stamp is only honoured when it's a type we know. On a gift card the
 *   underlying document's own `type` is the card format
 *   ("physicalCreditCardReceipt"), so an un-stamped row falls through to the
 *   shape inference below rather than being taken at face value.
 */
export const resolveActivityFilter = (
  tx: any
): Pick<ActivityFilter, "type" | "subtype"> => {
  if (tx?.utility)
    return {
      type: "billpayment",
      subtype: tx.utility?.type ?? tx?.subtype ?? "airtime",
    };
  if (KNOWN_TYPES.includes(tx?.type))
    return { type: tx.type, subtype: tx?.subtype };

  // Fallbacks for any row that arrives without a usable stamp.
  if (tx?.tradeValue || (tx?.card && typeof tx.card === "object"))
    return { type: "giftcard" };
  if (tx?.payoutAmount != null || tx?.recipient) return { type: "payout" };
  if (tx?.from && tx?.to) return { type: "crypto", subtype: "swap" };
  if (tx?.withdrawalId || tx?.toDeexUser)
    return { type: "crypto", subtype: "withdraw" };
  // Deposits are the only crypto row with an on-chain txid and no status.
  if (tx?.txid) return { type: "crypto", subtype: "deposit" };
  return { type: "crypto", subtype: tx?.subtype ?? "sell" };
};

/** Normalizes a row from the mixed `/recent` feed (type inferred per row). */
export const normalizeRecentTransaction = (tx: any): ActivityItem =>
  normalizeTransaction(tx, resolveActivityFilter(tx));

export const normalizeTransaction = (
  tx: any,
  filter: Pick<ActivityFilter, "type" | "subtype">
): ActivityItem => {
  switch (filter.type) {
    case "crypto":
      // The `all` feed returns mixed subtypes, so route by the per-transaction
      // `tx.subtype` when present, falling back to the filter's subtype.
      switch (tx?.subtype ?? filter.subtype) {
        case "deposit":
          return normalizeCryptoDeposit(tx);
        case "swap":
          return normalizeCryptoSwap(tx);
        case "withdraw":
          return normalizeCryptoWithdraw(tx);
        case "sell":
        default:
          return normalizeCryptoSell(tx);
      }
    case "billpayment":
      return normalizeBillPayment(tx, filter.subtype ?? "airtime");
    case "giftcard":
      return normalizeGiftCard(tx);
    case "payout":
    default:
      return normalizePayout(tx);
  }
};
